import { useState, useRef, useEffect, useCallback } from "react";
import "./styles.css";

const FABRIC_CDN = "https://cdnjs.cloudflare.com/ajax/libs/fabric.js/5.3.1/fabric.min.js";
const BBOX_COLOR = "rgb(60,123,255)";
const POLY_COLOR = "rgb(227,227,227)";

let counter = 0;
const uid = (t) => `${t}_${++counter}`;

export default function BBoxAnnotator({ initialWidth = 800, initialHeight = 600 }) {
  const canvasEl = useRef(null);
  const fc = useRef(null);
  const [ready, setReady] = useState(false);
  const [tool, setToolState] = useState("select");
  const toolRef = useRef("select");
  const [objects, setObjects] = useState([]);
  const [selId, setSelId] = useState(null);
  const selIdRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [props, setProps] = useState(null);
  const [polyPts, setPolyPts] = useState([]);
  const [bgName, setBgName] = useState(null);
  const [canvasSize, setCanvasSize] = useState({ w: initialWidth, h: initialHeight });
  const [showLabels, setShowLabels] = useState(false);
  const showLabelsRef = useRef(false);

  const drawState = useRef({ active: false, sx: 0, sy: 0, rect: null });
  const polyState = useRef({ points: [], cursorLine: null });

  function setTool(t) { toolRef.current = t; setToolState(t); }

  // Keep refs in sync
  useEffect(() => { selIdRef.current = selId; }, [selId]);
  useEffect(() => { showLabelsRef.current = showLabels; }, [showLabels]);

  // ─── Load Fabric ───
  useEffect(() => {
    if (window.fabric) { setReady(true); return; }
    const s = document.createElement("script");
    s.src = FABRIC_CDN;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, []);

  // // ─── Inject CSS (remove this if using import "./styles.css") ───
  // useEffect(() => {
  //   const el = document.createElement("style");
  //   el.textContent = CSS_TEXT;
  //   document.head.appendChild(el);
  //   return () => el.remove();
  // }, []);

  // ─── Init Canvas ───
  useEffect(() => {
    if (!ready || !canvasEl.current) return;
    const c = new window.fabric.Canvas(canvasEl.current, {
      width: initialWidth, height: initialHeight,
      backgroundColor: "#111118",
      selection: true, preserveObjectStacking: true,
    });
    fc.current = c;

    c.on("selection:created", (e) => handleSelect(e.selected?.[0]));
    c.on("selection:updated", (e) => handleSelect(e.selected?.[0]));
    c.on("selection:cleared", () => { setSelId(null); setProps(null); });
    c.on("object:modified", (e) => { syncAll(); if (e.target?._cid) handleSelect(e.target); });
    c.on("object:moving", onObjectMoving);
    c.on("object:scaling", onObjectMoving);

    c.on("mouse:down", onMouseDown);
    c.on("mouse:move", onMouseMove);
    c.on("mouse:up", onMouseUp);
    c.on("mouse:dblclick", () => { if (toolRef.current === "polygon") finishPoly(); });

    return () => c.dispose();
  }, [ready]);

  // ─── Live label update on move/scale ───
  function onObjectMoving(e) {
    if (!showLabelsRef.current) return;
    const obj = e.target;
    if (!obj?._cid) return;
    updateLabelsForObject(obj);
  }

  // ─── Selection handler ───
  function handleSelect(obj) {
    if (!obj?._cid) return;
    setSelId(obj._cid);
    buildProps(obj);
  }

  function buildProps(obj) {
    if (obj._ct === "bbox") {
      const b = obj.getBoundingRect(true);
      setProps({ type: "bbox", x: Math.round(b.left), y: Math.round(b.top), w: Math.round(b.width), h: Math.round(b.height) });
    } else if (obj._ct === "polygon") {
      const pts = getAbsolutePolyPoints(obj);
      setProps({ type: "polygon", points: pts });
    }
  }

  function getAbsolutePolyPoints(obj) {
    const matrix = obj.calcTransformMatrix();
    return obj.points.map(p => {
      const pt = window.fabric.util.transformPoint(
          { x: p.x - obj.pathOffset.x, y: p.y - obj.pathOffset.y }, matrix
      );
      return { x: Math.round(pt.x), y: Math.round(pt.y) };
    });
  }

  // ─── Sync objects ───
  function syncAll() {
    const c = fc.current; if (!c) return;
    setObjects(c.getObjects().filter(o => o._cid).map(o => {
      if (o._ct === "bbox") {
        const b = o.getBoundingRect(true);
        return { id: o._cid, type: "bbox", info: `${Math.round(b.left)},${Math.round(b.top)} ${Math.round(b.width)}×${Math.round(b.height)}` };
      }
      return { id: o._cid, type: "polygon", info: `${o.points?.length || 0} pts` };
    }));
    if (showLabelsRef.current) refreshAllLabels();
  }

  // ─── Labels on canvas ───
  function refreshAllLabels() {
    const c = fc.current; if (!c) return;
    // Remove old labels
    c.getObjects().filter(o => o._isLabel).forEach(o => c.remove(o));
    if (!showLabelsRef.current) { c.renderAll(); return; }
    // Add for each object
    c.getObjects().filter(o => o._cid).forEach(obj => addLabelsForObject(obj));
    c.renderAll();
  }

  function addLabelsForObject(obj) {
    const c = fc.current; if (!c) return;
    if (obj._ct === "bbox") {
      const b = obj.getBoundingRect(true);
      const x = Math.round(b.left), y = Math.round(b.top);
      const w = Math.round(b.width), h = Math.round(b.height);
      const label = new window.fabric.Text(
          `(${x}, ${y}) ${w}×${h}`,
          {
            left: b.left, top: b.top - 16,
            fontSize: 11, fill: BBOX_COLOR,
            fontFamily: "system-ui, sans-serif", fontWeight: "700",
            selectable: false, evented: false,
            _isLabel: true, _labelFor: obj._cid,
          }
      );
      c.add(label);
    } else if (obj._ct === "polygon") {
      const pts = getAbsolutePolyPoints(obj);
      pts.forEach((pt, i) => {
        const label = new window.fabric.Text(
            `${i}:(${pt.x},${pt.y})`,
            {
              left: pt.x + 6, top: pt.y - 6,
              fontSize: 10, fill: POLY_COLOR,
              fontFamily: "system-ui, sans-serif", fontWeight: "700",
              selectable: false, evented: false,
              _isLabel: true, _labelFor: obj._cid,
            }
        );
        c.add(label);
      });
    }
  }

  function updateLabelsForObject(obj) {
    const c = fc.current; if (!c) return;
    // Remove existing labels for this object
    c.getObjects().filter(o => o._isLabel && o._labelFor === obj._cid).forEach(o => c.remove(o));
    addLabelsForObject(obj);
    c.renderAll();
    // Also update props panel if this is selected
    if (selIdRef.current === obj._cid) buildProps(obj);
  }

  // ─── Toggle labels ───
  useEffect(() => {
    refreshAllLabels();
  }, [showLabels]);

  // ─── Tool switching ───
  useEffect(() => {
    const c = fc.current; if (!c) return;
    if (tool === "select") {
      c.selection = true;
      c.forEachObject(o => { if (o._cid) o.selectable = true; });
    } else {
      c.selection = false; c.discardActiveObject();
      c.forEachObject(o => { if (o._cid) o.selectable = false; });
    }
    c.renderAll();
    if (tool !== "polygon") clearPolyDraw();
  }, [tool]);

  // ─── Keyboard ───
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === "INPUT") return;
      if (e.key === "v") setTool("select");
      if (e.key === "b") setTool("bbox");
      if (e.key === "p") setTool("polygon");
      if (e.key === "Delete" || e.key === "Backspace") delSelected();
      if (e.key === "Escape") {
        if (toolRef.current === "polygon") finishPoly();
        setTool("select");
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  // ─── Mouse handlers ───
  function onMouseDown(opt) {
    const c = fc.current;
    const p = c.getPointer(opt.e);
    setMouse({ x: Math.round(p.x), y: Math.round(p.y) });
    const t = toolRef.current;

    if (t === "bbox") {
      drawState.current = { active: true, sx: p.x, sy: p.y, rect: null };
      const r = new window.fabric.Rect({
        left: p.x, top: p.y, width: 0, height: 0,
        fill: "rgba(60,123,255,.08)", stroke: BBOX_COLOR, strokeWidth: 2,
        strokeDashArray: [6, 3], selectable: false, evented: false,
      });
      c.add(r);
      drawState.current.rect = r;
    }

    if (t === "polygon") {
      polyState.current.points.push({ x: p.x, y: p.y });
      setPolyPts([...polyState.current.points]);
      c.add(new window.fabric.Circle({
        left: p.x - 5, top: p.y - 5, radius: 5,
        fill: POLY_COLOR, stroke: "#fff", strokeWidth: 1.5,
        selectable: false, evented: false, _tmp: true,
      }));
      if (polyState.current.points.length > 1) {
        const pts = polyState.current.points;
        const pv = pts[pts.length - 2];
        c.add(new window.fabric.Line([pv.x, pv.y, p.x, p.y], {
          stroke: POLY_COLOR, strokeWidth: 2, strokeDashArray: [5, 4],
          selectable: false, evented: false, _tmp: true,
        }));
      }
      c.renderAll();
    }
  }

  function onMouseMove(opt) {
    const c = fc.current;
    const p = c.getPointer(opt.e);
    setMouse({ x: Math.round(p.x), y: Math.round(p.y) });
    const t = toolRef.current;
    const ds = drawState.current;

    if (t === "bbox" && ds.active && ds.rect) {
      ds.rect.set({
        left: Math.min(ds.sx, p.x), top: Math.min(ds.sy, p.y),
        width: Math.abs(p.x - ds.sx), height: Math.abs(p.y - ds.sy),
      });
      c.renderAll();
    }

    if (t === "polygon" && polyState.current.points.length > 0) {
      if (polyState.current.cursorLine) c.remove(polyState.current.cursorLine);
      const last = polyState.current.points[polyState.current.points.length - 1];
      const l = new window.fabric.Line([last.x, last.y, p.x, p.y], {
        stroke: "rgba(227,227,227,.5)", strokeWidth: 1, strokeDashArray: [3, 3],
        selectable: false, evented: false, _tmp: true,
      });
      c.add(l);
      polyState.current.cursorLine = l;
      c.renderAll();
    }
  }

  function onMouseUp() {
    const c = fc.current;
    const t = toolRef.current;
    const ds = drawState.current;

    if (t === "bbox" && ds.active && ds.rect) {
      ds.active = false;
      const r = ds.rect;
      if (r.width < 5 || r.height < 5) {
        c.remove(r);
      } else {
        const id = uid("bbox");
        r.set({
          _cid: id, _ct: "bbox", selectable: true, evented: true,
          hasControls: true, hasBorders: true,
          cornerColor: BBOX_COLOR, cornerSize: 8, cornerStyle: "circle",
          transparentCorners: false, borderColor: BBOX_COLOR, strokeDashArray: null,
        });
        c.setActiveObject(r);
        handleSelect(r);
        syncAll();
      }
      ds.rect = null;
      setTool("select");
    }
  }

  // ─── Polygon finish ───
  const finishPoly = useCallback(() => {
    const c = fc.current;
    const pts = polyState.current.points;
    if (!c || pts.length < 3) { clearPolyDraw(); return; }

    c.getObjects().forEach(o => { if (o._tmp) c.remove(o); });
    if (polyState.current.cursorLine) { c.remove(polyState.current.cursorLine); polyState.current.cursorLine = null; }

    const id = uid("poly");
    const poly = new window.fabric.Polygon([...pts], {
      fill: "rgba(227,227,227,.06)", stroke: POLY_COLOR, strokeWidth: 2,
      selectable: true, evented: true, hasControls: true, hasBorders: true,
      cornerColor: POLY_COLOR, cornerSize: 8, cornerStyle: "circle",
      transparentCorners: false, borderColor: POLY_COLOR, _cid: id, _ct: "polygon",
    });
    c.add(poly);
    c.setActiveObject(poly);
    handleSelect(poly);

    polyState.current.points = [];
    setPolyPts([]);
    syncAll();
    setTool("select");
  }, []);

  function clearPolyDraw() {
    const c = fc.current; if (!c) return;
    c.getObjects().forEach(o => { if (o._tmp) c.remove(o); });
    if (polyState.current.cursorLine) { c.remove(polyState.current.cursorLine); polyState.current.cursorLine = null; }
    polyState.current.points = [];
    setPolyPts([]);
    c.renderAll();
  }

  // ─── CRUD ───
  function delSelected() {
    const c = fc.current; if (!c) return;
    const a = c.getActiveObject();
    if (a?._cid) {
      c.getObjects().filter(o => o._isLabel && o._labelFor === a._cid).forEach(o => c.remove(o));
      c.remove(a); c.discardActiveObject(); setSelId(null); setProps(null); syncAll();
    }
  }

  function delById(id) {
    const c = fc.current; if (!c) return;
    const o = c.getObjects().find(x => x._cid === id);
    if (o) {
      c.getObjects().filter(x => x._isLabel && x._labelFor === id).forEach(x => c.remove(x));
      c.remove(o);
      if (selIdRef.current === id) { c.discardActiveObject(); setSelId(null); setProps(null); }
      syncAll();
    }
  }

  function selectById(id) {
    const c = fc.current; if (!c) return;
    const o = c.getObjects().find(x => x._cid === id);
    if (o) { setTool("select"); c.setActiveObject(o); handleSelect(o); c.renderAll(); }
  }

  function clearAll() {
    const c = fc.current; if (!c) return;
    c.getObjects().filter(o => o._cid || o._tmp || o._isLabel).forEach(o => c.remove(o));
    c.discardActiveObject(); setSelId(null); setProps(null); setObjects([]); clearPolyDraw();
  }

  // ─── Image loading → resize canvas ───
  function loadBg(e) {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const c = fc.current;
      const img = new Image();
      img.onload = () => {
        // Fit to available space
        const area = document.querySelector(".canvas-area");
        const maxW = area.clientWidth - 40;
        const maxH = area.clientHeight - 40;
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        const newW = Math.round(img.width * scale);
        const newH = Math.round(img.height * scale);

        c.setDimensions({ width: newW, height: newH });
        setCanvasSize({ w: newW, h: newH });

        window.fabric.Image.fromURL(ev.target.result, (fImg) => {
          fImg.scaleToWidth(newW);
          c.setBackgroundImage(fImg, c.renderAll.bind(c));
        });

        setBgName(f.name);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
  }

  // ─── Resize canvas from inputs ───
  function resizeCanvas(key, val) {
    const num = parseInt(val, 10);
    const c = fc.current; if (!c) return;
    const newSize = { ...canvasSize, [key]: num };
    setCanvasSize(newSize);
    c.setDimensions({ width: newSize.w, height: newSize.h });
    c.renderAll();
  }

  // ─── Property text editing ───
  function updateBboxProp(key, val) {
    const c = fc.current; if (!c || !props) return;
    const num = parseInt(val, 10); if (isNaN(num)) return;
    const obj = c.getObjects().find(o => o._cid === selIdRef.current);
    if (!obj || obj._ct !== "bbox") return;
    const np = { ...props, [key]: num }; setProps(np);
    obj.set({ left: np.x, top: np.y, width: np.w, height: np.h, scaleX: 1, scaleY: 1 });
    obj.setCoords(); c.renderAll();
    if (showLabelsRef.current) updateLabelsForObject(obj);
    syncAll();
  }

  function updatePolyPoint(idx, axis, val) {
    const c = fc.current; if (!c || !props) return;
    const num = parseInt(val, 10); if (isNaN(num)) return;
    const obj = c.getObjects().find(o => o._cid === selIdRef.current);
    if (!obj || obj._ct !== "polygon") return;
    const newPts = props.points.map((p, i) => i === idx ? { ...p, [axis]: num } : { ...p });
    setProps({ ...props, points: newPts });
    obj.set({ points: newPts.map(p => ({ x: p.x, y: p.y })) });
    obj.set({ left: 0, top: 0, scaleX: 1, scaleY: 1, angle: 0, skewX: 0, skewY: 0 });
    obj._calcDimensions(); obj.setCoords(); c.renderAll();
    if (showLabelsRef.current) updateLabelsForObject(obj);
    syncAll();
  }

  if (!ready) {
    return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", background: "#0e0e14", color: "#8888a0" }}>Loading…</div>;
  }

  return (
      <div className="bbox-annotator">
        <div className="app">
          <aside className="sidebar">
            <div className="sidebar-header">
              <div className="logo"><div className="logo-block" /> ANNOTATOR</div>
            </div>

            <div className="tools">
              <button className={`tool-btn ${tool === "select" ? "active" : ""}`} onClick={() => setTool("select")}>SELECT<span className="key">V</span></button>
              <button className={`tool-btn ${tool === "bbox" ? "active" : ""}`} onClick={() => setTool("bbox")}>BBOX<span className="key">B</span></button>
              <button className={`tool-btn ${tool === "polygon" ? "active" : ""}`} onClick={() => setTool("polygon")}>POLY<span className="key">P</span></button>
            </div>

            <div className="actions">
              <button className="act-btn img-btn">
                {bgName ? `📷 ${bgName.slice(0, 14)}` : "Load Image"}
                <input type="file" accept="image/*" onChange={loadBg} />
              </button>
              <button className="act-btn danger" onClick={clearAll}>Clear All</button>
            </div>

            <div className="size-row">
              <span className="size-label">Canvas</span>
              <input className="size-input" type="number" value={canvasSize.w}
                     onChange={e => resizeCanvas("w", e.target.value)} />
              <span className="size-x">×</span>
              <input className="size-input" type="number" value={canvasSize.h}
                     onChange={e => resizeCanvas("h", e.target.value)} />
            </div>

            <div className="toggle-row">
              <span className="toggle-label">Show labels on canvas</span>
              <div
                  className={`toggle-switch ${showLabels ? "on" : ""}`}
                  onClick={() => setShowLabels(v => !v)}
              />
            </div>

            <div className="objects-section">
              <div className="section-title">Objects ({objects.length})</div>
              {objects.length === 0 ? (
                  <div className="empty-msg">Draw bboxes or polygons<br />on the canvas to start</div>
              ) : (
                  <div className="obj-list">
                    {objects.map(o => (
                        <div key={o.id} className={`obj-item ${selId === o.id ? "selected" : ""}`} onClick={() => selectById(o.id)}>
                          <span className={`obj-tag ${o.type}`}>{o.type === "bbox" ? "BB" : "PG"}</span>
                          <span className="obj-name">{o.id}</span>
                          <button className="obj-del" onClick={e => { e.stopPropagation(); delById(o.id); }}>×</button>
                        </div>
                    ))}
                  </div>
              )}
            </div>

            {props && selId && (
                <div className="props-section">
                  <div className="section-title">{selId}</div>
                  {props.type === "bbox" && (
                      <div className="props-grid">
                        {[["x", "X"], ["y", "Y"], ["w", "W"], ["h", "H"]].map(([k, label]) => (
                            <div className="prop-field" key={k}>
                              <label className="prop-label">{label}</label>
                              <input className="prop-input" type="number" value={props[k]}
                                     onChange={e => updateBboxProp(k, e.target.value)} />
                            </div>
                        ))}
                      </div>
                  )}
                  {props.type === "polygon" && (
                      <div className="poly-pts-editor">
                        {props.points.map((pt, i) => (
                            <div key={i} className="poly-pt-row">
                              <span className="poly-pt-label">{i}</span>
                              <input className="prop-input" type="number" value={pt.x}
                                     onChange={e => updatePolyPoint(i, "x", e.target.value)} />
                              <input className="prop-input" type="number" value={pt.y}
                                     onChange={e => updatePolyPoint(i, "y", e.target.value)} />
                            </div>
                        ))}
                      </div>
                  )}
                </div>
            )}
          </aside>

          <div className={`canvas-area ${tool !== "select" ? "drawing" : ""}`}>
            <div className="canvas-dots" />
            {tool === "bbox" && <div className="draw-hint">Click + drag to draw bbox</div>}
            {tool === "polygon" && polyPts.length === 0 && <div className="draw-hint">Click to place points · Double-click to finish</div>}
            {tool === "polygon" && polyPts.length > 0 && <div className="draw-hint">{polyPts.length} pts — double-click to close</div>}
            <div className="canvas-wrap"><canvas ref={canvasEl} /></div>
            <div className="coords">{mouse.x}, {mouse.y}</div>
            <div className="canvas-size-label">{canvasSize.w}×{canvasSize.h}</div>
          </div>
        </div>
      </div>
  );
}